/**
 * IntroPage Component
 * 
 * Non-technical introduction explaining the game and its educational purpose.
 * Uses a carousel to present information without scrolling.
 */

import React from 'react';
import { Carousel } from '@components/common/Carousel';
import styles from './IntroPage.module.scss';

interface IntroPageProps {
  onStart: () => void;
}

export const IntroPage: React.FC<IntroPageProps> = ({ onStart }) => {
  const slides = [
    // Slide 1: Welcome & How to Play
    <div key="slide1" className={styles.slide}>
      <h1>Welcome to the Escape Room Challenge!</h1>
      <h2>🎮 How to Play</h2>
      <p>
        You're trapped in a mysterious building with multiple rooms. Your goal is simple:{' '}
        <strong>visit every room exactly once</strong> to escape!
      </p>
      <p>
        But here's the catch: <strong>once you leave a room, its door locks behind you</strong>.
        You can never return to a room you've already visited.
      </p>
      <p>
        Choose your path wisely! If you get stuck with rooms still unvisited, you lose.
      </p>
    </div>,

    // Slide 2: Progressive Difficulty
    <div key="slide2" className={styles.slide}>
      <h2>📈 Progressive Difficulty</h2>
      <p>
        The game has <strong>6 levels</strong>, starting with just 4 rooms and growing to 15 rooms.
      </p>
      <p>
        As the number of rooms increases, finding the right path becomes exponentially harder.
        This demonstrates a real computer science concept called <strong>NP-completeness</strong>.
      </p>
    </div>,

    // Slide 3: Beat Your Best Time
    <div key="slide3" className={styles.slide}>
      <h2>⏱️ Beat Your Best Time</h2>
      <p>
        Each level is timed. Try to complete levels as quickly as possible and beat your
        personal best times!
      </p>
    </div>,

    // Slide 4: What You'll Learn
    <div key="slide4" className={styles.slide}>
      <h2>🧠 What You'll Learn</h2>
      <p>
        This game teaches you about the <strong>Hamiltonian Path Problem</strong>, a famous
        puzzle in mathematics and computer science.
      </p>
      <p>
        Finding a path that visits every location exactly once is easy for small numbers,
        but becomes incredibly difficult as the problem grows. This is why some problems
        that seem simple can actually be very hard for computers to solve!
      </p>
    </div>,
  ];

  return (
    <div className={styles.introPage}>
      <Carousel slides={slides} onComplete={onStart} />
    </div>
  );
};